import React, { useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { toast } from '../../../../ui/toast';
import { editJobOpening } from '../../../../utils/api_service/JobOpenings';
import {
  jobOpeningDetailsTabs,
  jobOpeningPages,
  jobOpeningTypes,
} from '../../../../utils/types/JobOpenings';

// Import our new hooks

// Components for rendering
import { AllApplicantAnalysis } from '../ApplicantAnalysis/AllApplicantAnalysis';
import AssessmentQuestions from '../AssessmentQuestions';
import { Interviews } from '../Interviews';
import { Psychometric } from '../Psychometric';
import JobOpeningDetails from '../JobOpeningDetails';
import { WellnessTab } from '../Wellness';
import StatusMessage from '../JobOpeningDetails/StatusMessage';
import LoadingSpinner from '../ui/LoaderSpinner';
import { useJobOpeningData } from './useJobOpeningData';
import { useJobOpeningForm } from './useJobOpeningForm';
import { useJobOpeningNavigation } from './useJobOpeningNavigation';

interface Step {
  label: jobOpeningPages;
  status: 'active' | 'non-active' | 'disabled';
}

const CompleteMappings: Record<jobOpeningPages, jobOpeningDetailsTabs[]> = {
  'Job Opening Details': [],
  'Auto-Assessments': ['Auto Assess Questions'],
  'Applicant Analysis': ['Job Requisition', 'Job Description', 'CV Ingestion'],
  'Smart Interviews': ['Job Requisition', 'Job Description', 'CV Ingestion'],
  Analytics: [],
  'Psychometric': [],
  'Wellness': [],
};

const MappingJobOpeningElement: Record<jobOpeningPages, jobOpeningTypes> = {
  'Job Opening Details': 'Job Opening Details',
  'Auto-Assessments': 'Job Opening Details',
  'Applicant Analysis': 'Applicant Analysis',
  'Smart Interviews': 'Smart Interviews',
  'Psychometric': 'Applicant Analysis',
  Analytics: 'Job Opening Details',
  Wellness: 'Wellness',
};

export const useCreateinterview = () => {
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const [show, setShow] = useState<boolean>(true);

  // Use our specialized hooks
  const {
    jobData,
    isLoading: dataLoading,
    jobTitle,
    jobOpeningElements,
    completedTabs,
    tabCounts,
    refetch,
  } = useJobOpeningData();
  const { currentPage, currentSubTab, navigateToTab, navigateToSubTab } =
    useJobOpeningNavigation();
  const formState = useJobOpeningForm(jobData, id);

  const [modalState, setModalState] = useState<boolean>(
    !id || id.trim() === '',
  );
  const [confirmDialogState, setConfirmDialogState] = useState(false);
  const [pageStatus, setPageStatus] =
    useState<'enabled' | 'disabled' | 'requiredCompletion' | 'loading'>(
      'loading',
    );
  const [requiredTabs, setRequiredTabs] = useState<string[]>([]);
  const [localJobTitle, setLocalJobTitle] = useState<string>('');

  // Sync from server data
  useEffect(() => {
    if (jobTitle) {
      setLocalJobTitle(jobTitle);
    }
  }, [jobTitle]);

  // Stages State (maintained for current integrations)
  const [stages, setStages] = useState([
    {
      label: 'Job Description',
      status: 'Inactive',
      currentSubStageIndex: 0,
      subStages: [
        { label: 'Inactive', status: 'Inactive' },
        { label: 'Pending', status: 'Pending' },
        { label: 'Completed', status: 'Completed' },
      ],
    },
    {
      label: 'Job Post',
      status: 'Inactive',
      currentSubStageIndex: 0,
      subStages: [
        { label: 'Inactive', status: 'Inactive' },
        { label: 'Pending', status: 'Pending' },
        { label: 'In Progress', status: 'In Progress' },
        { label: 'Completed', status: 'Completed' },
      ],
    },
    {
      label: 'Shortlisting',
      status: 'Inactive',
      currentSubStageIndex: 0,
      subStages: [
        { label: 'Inactive', status: 'Inactive' },
        { label: 'In Progress', status: 'In Progress' },
        { label: 'Completed', status: 'Completed' },
      ],
    },
    {
      label: 'Interviews',
      status: 'Inactive',
      currentSubStageIndex: 0,
      subStages: [
        { label: 'Inactive', status: 'Inactive' },
        { label: 'In Progress', status: 'In Progress' },
        { label: 'Completed', status: 'Completed' },
      ],
    },
    {
      label: 'Decision Making',
      status: 'Inactive',
      currentSubStageIndex: 0,
      subStages: [
        { label: 'Inactive', status: 'Inactive' },
        { label: 'Decision in Progress', status: 'Decision in Progress' },
        { label: 'Offer in Progress', status: 'Offer in Progress' },
        { label: 'Completed', status: 'Completed' },
      ],
    },
    {
      label: 'Closed',
      status: 'Inactive',
      currentSubStageIndex: 0,
      subStages: [
        { label: 'Inactive', status: 'Inactive' },
        { label: 'Offer Accepted', status: 'Offer Accepted' },
        { label: 'No Suitable Candidate', status: 'No Suitable Candidate' },
        { label: 'No Offers Accepted', status: 'No Offers Accepted' },
        { label: 'Job Request Withdrawn', status: 'Job Request Withdrawn' },
      ],
    },
  ]);

  // Sync stages from jobData
  useEffect(() => {
    if (jobData?.status) {
      const statusUpdate = jobData.status;
      const keyToLabelMap: Record<string, string> = {
        jobDescription: 'Job Description',
        jobPost: 'Job Post',
        shortlisting: 'Shortlisting',
        interviews: 'Interviews',
        decisionMaking: 'Decision Making',
        closed: 'Closed',
      };

      setStages((prevStages) =>
        prevStages.map((stage) => {
          const matchKey = Object.keys(keyToLabelMap).find(
            (key) => keyToLabelMap[key] === stage.label,
          );
          if (!matchKey) return stage;
          const newStatus = statusUpdate[matchKey as keyof typeof statusUpdate];
          if (!newStatus) return stage;
          const newSubIndex = stage.subStages.findIndex(
            (sub) =>
              sub.label.toLowerCase() === newStatus.toLowerCase() ||
              sub.status.toLowerCase() === newStatus.toLowerCase(),
          );
          return {
            ...stage,
            status: newStatus,
            currentSubStageIndex:
              newSubIndex >= 0 ? newSubIndex : stage.currentSubStageIndex,
          };
        }),
      );
    }
  }, [jobData]);

  // Page Status Logic
  useEffect(() => {
    if (dataLoading) {
      setPageStatus('loading');
      return;
    }

    const requiredElement = MappingJobOpeningElement[currentPage];
    if (
      currentPage === 'Job Opening Details' ||
      currentPage === 'Applicant Analysis' ||
      currentPage === 'Psychometric' ||
      currentPage === 'Wellness' ||
      currentPage === 'Smart Interviews'
    ) {
      setPageStatus('enabled');
      return;
    }

    if (!jobOpeningElements.includes(requiredElement)) {
      setPageStatus('disabled');
    } else {
      const requiredTabsForPage = CompleteMappings[currentPage];
      const incomplete = requiredTabsForPage.filter(
        (tab) => !completedTabs.includes(tab),
      );
      setRequiredTabs(incomplete);
      setPageStatus(incomplete.length === 0 ? 'enabled' : 'requiredCompletion');
    }
  }, [currentPage, jobOpeningElements, completedTabs, dataLoading]);

  // Refetch data when navigating to Job Opening Details to ensure freshness
  useEffect(() => {
    if (
      id &&
      currentPage === 'Job Opening Details' &&
      typeof refetch === 'function'
    ) {
      refetch();
    }
  }, [id, currentPage, refetch]);

  const steps: Step[] = useMemo(() => {
    const labels: jobOpeningPages[] = [
      'Job Opening Details',
      'Applicant Analysis',
      'Auto-Assessments',
      'Smart Interviews',
      'Psychometric',
      'Wellness',
    ];
    return labels.map((label) => ({
      label,
      status:
          label === currentPage
              ? 'active'
              : jobOpeningElements.includes(label)
                  ? 'non-active'
                  : 'disabled',
    }));
  }, [currentPage, jobOpeningElements]);

  const enableElement = async () => {
    try {
      const elements = Array.from(
        new Set([
          ...jobOpeningElements,
          MappingJobOpeningElement[currentPage] as jobOpeningTypes,
        ]),
      );
      const response = await editJobOpening(elements, id);
      if (!response.ok) toast.error('Error Updating Job Opening');
    } catch {
      toast.error('Error Updating Job Opening');
    }
  };

  const handleModalToggle = () => {
    setModalState(false);
    history.push('/job-openings-list');
    setConfirmDialogState(false);
  };

  const renderFormPage = () => {
    if (dataLoading || pageStatus === 'loading') return <LoadingSpinner />;
    if (pageStatus !== 'enabled') {
      return (
        <StatusMessage
            status={pageStatus}
            requiredTabs={requiredTabs}
            enabledElement={enableElement}
            setCurrentPage={navigateToTab}
            setStatusTab={navigateToSubTab}
        />
      );
    }

    switch (currentPage) {
      case 'Job Opening Details':
        return (
          <JobOpeningDetails
              tab={currentSubTab}
              stages={stages}
              setStages={setStages}
              jobTitleLabel={localJobTitle}
              setJobTitleLabel={setLocalJobTitle}
              {...formState}
          />
        );
      case 'Auto-Assessments':
        return <AssessmentQuestions />;
      case 'Applicant Analysis':
        return <AllApplicantAnalysis jobOpeningElements={jobOpeningElements}/>;
      case 'Smart Interviews':
        return <Interviews />;
      case 'Analytics':
        return <></>;
      case 'Wellness':
        return <WellnessTab jobOpeningId={id ?? ''} />;
      case 'Psychometric':
        return <Psychometric/>;
      default:
        return (
          <StatusMessage
              status='disabled'
              requiredTabs={[]}
              enabledElement={async () => {}}
              setCurrentPage={navigateToTab}
              setStatusTab={navigateToSubTab}
          />
        );
    }
  };

  return {
    ...formState,
    jobTitleLabel: localJobTitle,
    modalState,
    setModalState,
    confirmDialogState,
    setConfirmDialogState,
    steps,
    currentPage,
    setCurrentPage: navigateToTab,
    renderFormPage,
    handleModalToggle,
    stages,
    setStages,
    jobOpeningElements,
    setJobOpeningElements: () => {},
    completedJobOpeningDetails: completedTabs,
    setCompletedJobOpeningDetails: () => {},
    setShow,
    show,
    tabCounts,
  };
};
